'use client';

import { useCallback } from 'react';
import {
	BigNum,
	MarketType,
	PositionDirection,
	getLimitOrderParams,
	getTriggerLimitOrderParams,
	getTriggerMarketOrderParams,
	OrderTriggerCondition,
	PostOnlyParams,
	PRICE_PRECISION,
	QUOTE_PRECISION_EXP,
	PRICE_PRECISION_EXP,
	OrderParamsBitFlag,
	MAX_LEVERAGE_ORDER_SIZE,
	ZERO,
	BN,
	User,
	PublicKey,
	QUOTE_SPOT_MARKET_INDEX,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	UIOrderType,
	EMPTY_AUCTION_PARAMS,
	UIMarket,
	getUserKey,
} from '@velocity-exchange/common';
import { SpotTradeFormOutputProps } from 'src/stores/DriftStore/actions/actionHelpers/orderPrep';
import { ORDER_PREP_UTILS } from 'src/stores/DriftStore/actions/actionHelpers/orderPrep';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import useDriftClient from 'src/hooks/useDriftClient';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import { syncGetCurrentSettings } from 'src/environmentVariables/EnvironmentVariables';
import { SlippageTolerance } from 'src/@types/types';
import NumLib from 'src/utils/NumLib';
import { orderTypeSwitch } from 'src/utils/trade';
import { getOrderToastMessage } from 'src/stores/DriftStore/helpers';
import { dlog } from 'src/dev';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';
import { OrderFillTimeTracker } from 'src/utils/orderFillTimeTracker';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { updateAfterTx } from 'src/stores/DriftStore/helpers';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import useGetRawOracleDataForMarket from 'src/hooks/useGetRawOracleDataForMarket';
import { useAutoSignDriftActionWithFallback } from 'src/hooks/wallets/useAutoSignDriftActionWithFallback';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import invariant from 'tiny-invariant';
import useCurrentSlotRef from 'src/hooks/useCurrentSlotRef';

type OpenSpotOrderProps = SpotTradeFormOutputProps & {
	oraclePrice: BN;
};

/**
 * Hook for opening spot orders (both market and non-market orders)
 */
export const useOpenSpotOrder = () => {
	const driftClient = useDriftClient();
	const walletAdapter = useDriftStore((s) => s.wallet.current.adapter);
	const currentUserAccount = useCurrentUserAccount();
	const appEventEmitter = useAppEventEmitter();
	const isGeoblocked = useDriftStore((s) => s.isGeoblocked);
	const currentSlotRef = useCurrentSlotRef();

	const getTxParams = useGetTxParams();
	const accountsGet = useDriftAccountsStore((s) => s.get);
	const setDriftStore = useDriftStore((s) => s.set);
	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const getRawOracleData = useGetRawOracleDataForMarket();

	const openSpotMarketOrder = useCallback(
		async (
			signerWallet: UiEnhancedIWalletV2,
			isAutoSigned: boolean,
			props: SpotTradeFormOutputProps,
			existingToastId?: string
		): Promise<boolean> => {
			if (!driftClient || !currentUserAccount) return false;

			const marketId = MarketId.createSpotMarket(props.targetMarketIndex);
			let toastId = existingToastId ?? '';

			try {
				const { userAccount, auctionToastEventProps, ...preppedParams } =
					await ORDER_PREP_UTILS.prepSpotMarketOrderParams(props, {
						getUserAccountData: () => {
							return accountsGet().getCurrentUserAccount() as {
								client: User;
								pubKey: PublicKey;
							};
						},
						getDriftClient: () => driftClient,
						getPriceInfo: (marketId: MarketId) => {
							const oraclePrice = getOraclePriceForMarket(marketId);
							const rawOracle = getRawOracleData(marketId);
							return {
								markPrice: oraclePrice,
								bestBid: oraclePrice,
								bestAsk: oraclePrice,
								updateSlot: rawOracle?.slot?.toNumber() ?? 0,
							};
						},
						getOraclePriceData: (marketId: MarketId) =>
							getOraclePriceForMarket(marketId).val,
					});
				toastId = toastId || auctionToastEventProps.toastId;

				await driftClient.switchActiveUser(
					userAccount.subAccountId,
					userAccount.authority
				);

				const auctionVersion = useFeatureFlagStore
					.getState()
					.flagEnabled('AUCTION_PARAMS_VERSION')
					? 2
					: 1;

				dlog(
					`stuck_toasts`,
					`placing_spot_market_order_toast .. toastId : ${toastId}`
				);

				appEventEmitter.emit('expectedAuctionInit', {
					...auctionToastEventProps,
					isAutoSigned,
				});

				const txParams = getTxParams({ marketId });

				const spotOrderIx = await driftClient.getPlaceSpotOrderIx(
					preppedParams.orderParams,
					undefined,
					{
						authority: signerWallet.publicKey,
					}
				);

				const txSig = await signerWallet.signAndSendTransaction(
					spotOrderIx,
					txParams
				);

				OrderFillTimeTracker.trackOrderSubmission(
					preppedParams.nextOrderId,
					Date.now(),
					preppedParams.orderParams,
					'legacy',
					preppedParams.userAccountPublicKey.toString(),
					auctionVersion,
					undefined,
					preppedParams.oraclePrice
				);

				appEventEmitter.emit('expectedAuctionTxSigned', {
					...auctionToastEventProps,
					signedTxData: [],
				});

				appEventEmitter.emit(
					'expectedAuctionTxConfirmed',
					auctionToastEventProps
				);

				console.log(`Sent market order with txSig : ${txSig}`);

				setDriftStore((s) => {
					// @ts-ignore
					s.tradeForm.baseSizeStringValue = '';
					// @ts-ignore
					s.tradeForm.quoteSizeStringValue = '';
				});

				dlog(`stuck_toasts`, `order_confirmed`);
				appEventEmitter.emit('tradeConfirmation');

				const userKey = getUserKey(
					userAccount.subAccountId,
					userAccount.authority
				);

				const accountsState = accountsGet();
				const userAccountClient = accountsState.accounts[userKey]?.client;
				if (userAccountClient) {
					updateAfterTx(driftClient, userAccountClient);
				}

				return true;
			} catch (e: any) {
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter as any,
				});
				return false;
			}
		},
		[
			driftClient,
			walletAdapter,
			appEventEmitter,
			currentUserAccount,
			getTxParams,
			accountsGet,
			setDriftStore,
			getOraclePriceForMarket,
			getRawOracleData,
		]
	);

	const openSpotNonMarketOrder = useCallback(
		async (
			signerWallet: UiEnhancedIWalletV2,
			props: OpenSpotOrderProps,
			existingToastId?: string
		): Promise<boolean> => {
			const {
				side,
				baseSizeStringValue,
				orderType,
				priceBoxStringValue,
				secondaryPriceBoxStringValue,
				reduceOnly,
				slippageTolerance,
				postOnly,
				targetMarketIndex,
				immediateOrCancel,
				currentPositionDirection,
				oraclePrice,
				maxLeverageSelected,
				spotMarketAccount,
			} = props;

			const marketId = MarketId.createSpotMarket(targetMarketIndex);
			if (
				!baseSizeStringValue ||
				!orderType ||
				!driftClient ||
				!currentUserAccount
			)
				return false;

			const toastId = existingToastId || `${orderType}-${side}-${Date.now()}`;

			try {
				const basePrecisionToUse =
					UIMarket.spotMarkets[targetMarketIndex].precisionExp;
				const stepSizeBn = spotMarketAccount.orderStepSize;
				const baseSizeBn = BigNum.fromPrint(
					baseSizeStringValue,
					basePrecisionToUse
				).val;
				const baseSizeAfterStep = baseSizeBn.div(stepSizeBn).mul(stepSizeBn);
				const baseSize = BigNum.from(baseSizeAfterStep, basePrecisionToUse);

				const priceBoxValue = Number(priceBoxStringValue);
				const secondaryPriceBoxValue = Number(secondaryPriceBoxStringValue);

				const currentPositionIsShort = currentPositionDirection === 'short';
				const targetDirection =
					side === 'buy' ? PositionDirection.LONG : PositionDirection.SHORT;

				let useMaxLeverageFlag = false;
				if (maxLeverageSelected && currentUserAccount?.client) {
					// Check max leverage flag usage
					const tradeQuoteSize = baseSize
						.shiftTo(PRICE_PRECISION_EXP)
						.mul(
							BigNum.fromPrint(
								priceBoxStringValue ?? oraclePrice.toString(),
								PRICE_PRECISION_EXP
							)
						)
						.shiftTo(QUOTE_PRECISION_EXP);
					const userMaxTradeSizeUsdc = BigNum.from(
						currentUserAccount.client.getMaxTradeSizeUSDCForSpot(
							targetMarketIndex,
							targetDirection
						),
						QUOTE_PRECISION_EXP
					);

					const onePercent = userMaxTradeSizeUsdc.scale(1, 100);
					useMaxLeverageFlag = userMaxTradeSizeUsdc
						.sub(tradeQuoteSize)
						.abs()
						.lt(onePercent);
				}

				notify({
					id: toastId,
					type: 'awaiting_wallet_confirmation',
					showUntilCancelled: true,
				});

				const orderBaseSize = useMaxLeverageFlag
					? MAX_LEVERAGE_ORDER_SIZE
					: baseSize.val;

				const inputPrice = BigNum.fromPrint(
					priceBoxStringValue ?? oraclePrice.toString(),
					PRICE_PRECISION_EXP
				);

				const settings = syncGetCurrentSettings();
				if (!settings) throw new Error('Settings not found');

				const limitAuctionParams = postOnly
					? EMPTY_AUCTION_PARAMS
					: await ORDER_PREP_UTILS.getLimitOrderAuctionParams({
							marketId,
							limitPrice: inputPrice,
							direction: targetDirection,
							harnessProps: {
								getUserAccountData: () => {
									return accountsGet().getCurrentUserAccount() as {
										client: User;
										pubKey: PublicKey;
									};
								},
								getDriftClient: () => driftClient,
								getPriceInfo: (marketId: MarketId) => {
									const oraclePrice = getOraclePriceForMarket(marketId);
									const rawOracle = getRawOracleData(marketId);
									return {
										markPrice: oraclePrice,
										bestBid: oraclePrice,
										bestAsk: oraclePrice,
										updateSlot: rawOracle?.slot?.toNumber() ?? 0,
									};
								},
								getOraclePriceData: (marketId: MarketId) =>
									getOraclePriceForMarket(marketId).val,
							},
							amount: orderBaseSize,
							maxLeverageSelected: useMaxLeverageFlag,
							reduceOnly: reduceOnly ?? false,
							slippageTolerance: slippageTolerance as SlippageTolerance,
						});

				const orderParams = orderTypeSwitch<Exclude<UIOrderType, 'market'>>(
					orderType as Exclude<UIOrderType, 'market'>,
					{
						limit: getLimitOrderParams({
							marketIndex: targetMarketIndex,
							marketType: MarketType.SPOT,
							direction: targetDirection,
							baseAssetAmount: orderBaseSize,
							price: NumLib.formatNum.toRawBn(priceBoxValue, PRICE_PRECISION),
							reduceOnly,
							postOnly: postOnly
								? PostOnlyParams.MUST_POST_ONLY
								: PostOnlyParams.NONE,
							bitFlags: immediateOrCancel
								? OrderParamsBitFlag.ImmediateOrCancel
								: undefined,
							...limitAuctionParams,
						}),
						stopMarket: getTriggerMarketOrderParams({
							marketIndex: targetMarketIndex,
							marketType: MarketType.SPOT,
							direction: targetDirection,
							baseAssetAmount: orderBaseSize,
							triggerPrice: NumLib.formatNum.toRawBn(
								priceBoxValue,
								PRICE_PRECISION
							),
							triggerCondition: currentPositionIsShort
								? OrderTriggerCondition.ABOVE
								: OrderTriggerCondition.BELOW,
							reduceOnly,
						}),
						stopLimit: getTriggerLimitOrderParams({
							marketIndex: targetMarketIndex,
							marketType: MarketType.SPOT,
							direction: targetDirection,
							baseAssetAmount: orderBaseSize,
							triggerPrice: NumLib.formatNum.toRawBn(
								priceBoxValue,
								PRICE_PRECISION
							),
							price: NumLib.formatNum.toRawBn(
								secondaryPriceBoxValue,
								PRICE_PRECISION
							),
							triggerCondition: currentPositionIsShort
								? OrderTriggerCondition.ABOVE
								: OrderTriggerCondition.BELOW,
							reduceOnly,
						}),
						takeProfitMarket: getTriggerMarketOrderParams({
							marketIndex: targetMarketIndex,
							marketType: MarketType.SPOT,
							direction: targetDirection,
							baseAssetAmount: orderBaseSize,
							triggerPrice: NumLib.formatNum.toRawBn(
								priceBoxValue,
								PRICE_PRECISION
							),
							triggerCondition: currentPositionIsShort
								? OrderTriggerCondition.BELOW
								: OrderTriggerCondition.ABOVE,
							reduceOnly,
						}),
						takeProfitLimit: getTriggerLimitOrderParams({
							marketIndex: targetMarketIndex,
							marketType: MarketType.SPOT,
							direction: targetDirection,
							baseAssetAmount: orderBaseSize,
							triggerPrice: NumLib.formatNum.toRawBn(
								priceBoxValue,
								PRICE_PRECISION
							),
							price: NumLib.formatNum.toRawBn(
								secondaryPriceBoxValue,
								PRICE_PRECISION
							),
							triggerCondition: currentPositionIsShort
								? OrderTriggerCondition.BELOW
								: OrderTriggerCondition.ABOVE,
							reduceOnly,
						}),
						oracleLimit: getLimitOrderParams({
							marketIndex: targetMarketIndex,
							marketType: MarketType.SPOT,
							direction: targetDirection,
							baseAssetAmount: orderBaseSize,
							price: ZERO,
							reduceOnly,
							postOnly: postOnly
								? PostOnlyParams.MUST_POST_ONLY
								: PostOnlyParams.NONE,
							bitFlags: immediateOrCancel
								? OrderParamsBitFlag.ImmediateOrCancel
								: undefined,
							oraclePriceOffset: new BN(
								Math.round(priceBoxValue * PRICE_PRECISION.toNumber())
							),
						}),
						// these are routed through the market orders function
						oracle: undefined,
						// scaled orders not support for spot yet
						scaledOrders: undefined,
					}
				);

				const spotOrderIx = await driftClient.getPlaceSpotOrderIx(
					orderParams,
					undefined,
					{
						authority: signerWallet.publicKey,
					}
				);

				await signerWallet.signAndSendTransaction(
					spotOrderIx,
					getTxParams({ marketId })
				);

				// follows internal implementation of VelocityClient.placeSpotOrder()
				driftClient.spotMarketLastSlotCache.set(
					targetMarketIndex,
					currentSlotRef.current
				);
				driftClient.spotMarketLastSlotCache.set(
					QUOTE_SPOT_MARKET_INDEX,
					currentSlotRef.current
				);

				const completedOrderMessage = getOrderToastMessage({
					orderType: orderType as any,
				});

				notify({
					type: 'success',
					message: completedOrderMessage,
					id: toastId,
					updatePrevious: true,
				});
				return true;
			} catch (e: any) {
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter as any,
				});
				return false;
			}
		},
		[
			driftClient,
			walletAdapter,
			currentUserAccount,
			getTxParams,
			accountsGet,
			getOraclePriceForMarket,
			getRawOracleData,
		]
	);

	const openSpotOrder = useAutoSignDriftActionWithFallback(
		useCallback(
			async (
				signerWallet: UiEnhancedIWalletV2,
				isAutoSigned: boolean,
				toastId: string,
				props: OpenSpotOrderProps
			): Promise<boolean> => {
				invariant(driftClient, 'driftClient is required');

				if (!currentUserAccount) return false;

				const { baseSizeStringValue, orderType } = props;
				if (!baseSizeStringValue || !orderType) return false;

				if (isGeoblocked && !props.reduceOnly) {
					notify({
						type: 'error',
						message: 'Geoblocked',
						description:
							'You are not allowed to use Drift from a restricted territory',
						lengthMs: 5000,
					});
					return false;
				}

				// Handle market orders
				if (orderType === 'market') {
					return await openSpotMarketOrder(
						signerWallet,
						isAutoSigned,
						props,
						toastId
					);
				}

				// Handle non-market orders (limit, stop, etc.)
				return await openSpotNonMarketOrder(signerWallet, props, toastId);
			},
			[
				isGeoblocked,
				driftClient,
				currentUserAccount,
				openSpotMarketOrder,
				openSpotNonMarketOrder,
			]
		)
	);

	return openSpotOrder;
};
